<template>
  <v-card class="card-container">
    <div class="main-toolbar">
      <div class="icon-back">
        <v-img src="../../../assets/figmaIcons/white/standchecklist.svg" max-width="55%"></v-img>
      </div>
      <span class="main-toolbar-title">Base de conhecimento</span>
      <v-spacer></v-spacer>
    </div>

    <div class="mt-4">
      <v-card class="pa-4 mb-6">

        <!-- Toolbar desktop -->
        <div class="d-flex align-center justify-end" v-if="$vuetify.breakpoint.mdAndUp">
          <v-btn color="#0F3565" dark depressed class="primary-button" @click="openDialog">
            <v-icon left small>mdi-plus</v-icon>
            Adicionar conhecimento
          </v-btn>
          <v-btn class="ml-2 primary-button" color="#BBCF20" depressed dark @click="visible = !visible">
            <v-icon left small>mdi-filter-variant</v-icon>
            Filtros
          </v-btn>
        </div>

        <!-- Toolbar mobile -->
        <div class="d-flex justify-left back mt-4" v-if="$vuetify.breakpoint.smAndDown">
          <v-btn color="#0F3565" dark depressed small @click="openDialog">
            <v-icon center>mdi-plus</v-icon>
          </v-btn>
          <v-btn small class="ml-2 mb-2" color="#BBCF20" depressed dark @click="visible = !visible">
            <v-icon center>mdi-filter-variant</v-icon>
          </v-btn>
        </div>

        <!-- Filtros -->
        <v-expand-transition>
          <div v-show="visible" class="mt-4">
            <v-row class="ma-0" dense align="end">
              <v-col class="py-0" cols="12" sm="4" md="4">
                <label class="label">Buscar</label>
                <v-text-field v-model="searchQuery" placeholder="Buscar por titulo..."
                  hide-details="auto" outlined class="primary-input" @keyup.enter="searchKnowledge"
                  clearable @click:clear="clearSearch"></v-text-field>
              </v-col>
              <v-col class="py-0" cols="6" sm="3" md="3">
                <label class="label">Tipo</label>
                <v-select v-model="filterType" :items="filterTypeOptions" item-text="text"
                  item-value="value" placeholder="Todos" hide-details="auto" outlined
                  background-color="#F5F5F5" class="primary-input" append-icon="mdi-chevron-down"
                  clearable @change="applyFilter"></v-select>
              </v-col>
              <v-col class="py-0" cols="6" sm="3" md="3">
                <label class="label">Status</label>
                <v-select v-model="filterActive" :items="filterOptions" item-text="text"
                  item-value="value" placeholder="Todos" hide-details="auto" outlined
                  background-color="#F5F5F5" class="primary-input" append-icon="mdi-chevron-down"
                  @change="applyFilter"></v-select>
              </v-col>
              <v-col class="py-0 d-flex align-end" cols="12" sm="2" md="2">
                <v-btn class="secondary-button" depressed dark @click="searchKnowledge" block>
                  <v-icon left small>mdi-magnify</v-icon>
                  Filtrar
                </v-btn>
              </v-col>
            </v-row>

            <!-- Chips dos filtros ativos -->
            <div class="d-flex flex-wrap mt-2 ml-1" v-if="hasActiveFilters">
              <v-chip v-if="activeSearchQuery" small close class="mr-2 mb-1"
                color="#0F3565" text-color="#fff" @click:close="clearSearch">
                Busca: {{ activeSearchQuery }}
              </v-chip>
              <v-chip v-if="filterType !== null" small close class="mr-2 mb-1"
                :color="getTypeColor(filterType)" text-color="#fff" @click:close="filterType = null; applyFilter()">
                {{ getTypeName(filterType) }}
              </v-chip>
              <v-chip v-if="filterActive !== null" small close class="mr-2 mb-1"
                :color="filterActive ? '#4CAF50' : '#9E9E9E'" text-color="#fff" @click:close="filterActive = null; applyFilter()">
                {{ filterActive ? 'Ativos' : 'Inativos' }}
              </v-chip>
              <v-btn x-small text color="#999" class="mb-1" @click="clearAllFilters">
                <v-icon x-small left>mdi-cached</v-icon>
                Limpar filtros
              </v-btn>
            </div>
          </div>
        </v-expand-transition>

        <!-- Desktop Table -->
        <div class="wrapper desktop mt-4">
          <v-data-table :headers="headers" :items="knowledges" :loading="loading" :page.sync="page"
            :items-per-page="itemsPerPage" mobile-breakpoint="0" :server-items-length="totalItems"
            class="main-table row-pointer" hide-default-footer hide-default-header
            loading-text="Carregando..." @click:row="editKnowledge">

            <template #header="{ props, on }">
              <thead>
                <tr v-if="props && props.headers">
                  <th class="text-center" :class="`col-${header.value}`" v-for="header in props.headers"
                    :key="header.value">
                    <div>
                      <v-hover v-slot="{ hover }">
                        <div>
                          <span>{{ header.text }}</span>
                          <v-icon v-if="hover && header.sortable !== false" color="white" class="mx-2"
                            @click.stop="on.sort(header.value)">
                            {{
                              props.options && props.options.sortBy && props.options.sortBy.includes(header.value)
                                ? `mdi-chevron-down`
                                : `mdi-chevron-up`
                            }}
                          </v-icon>
                        </div>
                      </v-hover>
                    </div>
                  </th>
                </tr>
              </thead>
            </template>

            <template #[`item.title`]="{ item }">
              <div class="d-flex align-center py-2">
                <v-icon small :color="getTypeColor(item.type)" class="mr-2">{{ getTypeIcon(item.type) }}</v-icon>
                <span class="font-weight-medium title-cell">{{ item.title }}</span>
              </div>
            </template>

            <template #[`item.content`]="{ item }">
              <v-tooltip bottom max-width="400" :disabled="item.content.length <= 100">
                <template v-slot:activator="{ on, attrs }">
                  <span class="content-cell" v-bind="attrs" v-on="on">{{ item.content }}</span>
                </template>
                <span style="white-space: pre-wrap;">{{ item.content.substring(0, 500) }}{{ item.content.length > 500 ? '...' : '' }}</span>
              </v-tooltip>
            </template>

            <template #[`item.type`]="{ item }">
              <v-chip small :color="getTypeColor(item.type)" text-color="#fff" class="font-weight-medium">
                {{ getTypeName(item.type) }}
              </v-chip>
            </template>

            <template #[`item.isActive`]="{ item }">
              <v-chip small :color="item.isActive ? '#4CAF50' : '#BDBDBD'" text-color="#fff">
                <v-icon x-small left>{{ item.isActive ? 'mdi-check-circle' : 'mdi-close-circle' }}</v-icon>
                {{ item.isActive ? 'Ativo' : 'Inativo' }}
              </v-chip>
            </template>

            <template #[`item.createdAt`]="{ item }">
              <span class="text-caption grey--text text--darken-1">{{ formatDate(item.createdAt) }}</span>
            </template>

            <template #[`item.actions`]="{ item }">
              <div class="d-flex justify-center actions-cell">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small class="ma-1" color="#0F3565" @click.stop="editKnowledge(item)"
                      v-bind="attrs" v-on="on">
                      <v-icon small>mdi-pencil-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Editar</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small class="ma-1" color="error" v-bind="attrs"
                      @click.stop="openDeleteDialog(item)" v-on="on">
                      <v-icon small>mdi-trash-can-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Excluir</span>
                </v-tooltip>
              </div>
            </template>

            <!-- Empty state -->
            <template #no-data>
              <div class="text-center py-8">
                <v-icon size="64" color="#BDBDBD" class="mb-4">mdi-book-open-variant</v-icon>
                <div class="text-h6 grey--text text--darken-1 mb-2">Nenhum conhecimento cadastrado</div>
                <div class="text-body-2 grey--text mb-4">Adicione artigos para que o Felix possa responder perguntas sobre o sistema</div>
                <v-btn color="#0F3565" dark depressed @click="openDialog">
                  <v-icon left small>mdi-plus</v-icon>
                  Adicionar primeiro conhecimento
                </v-btn>
              </div>
            </template>

          </v-data-table>

          <div class="pagination" v-if="knowledges.length > 0">
            <div class="item-pagination block-one">
              <v-menu bottom offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" class="btn" depressed color="#C0C0C0" small>
                    qtd: {{ itemsPerPage }}
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item v-for="(size, i) in sizes" :key="i" @click="setPageSize(size)">
                    <v-list-item-title>{{ size }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
              <div class="registros" v-if="totalItensOnPage">
                <span> {{ paginationStart }} - {{ paginationEnd }} de {{ totalItems }} </span>
              </div>
            </div>

            <div class="item-pagination block-two">
              <v-pagination class="pagination-item" v-model="page" :length="totalPages"
                :total-visible="3"></v-pagination>
            </div>
          </div>
        </div>

        <!-- Mobile Table -->
        <div class="wrapper mobile mt-4">
          <v-data-table :headers="headersMobile" :items="knowledges" :loading="loading" :page.sync="page"
            :items-per-page="itemsPerPage" mobile-breakpoint="330" :server-items-length="totalItems"
            class="main-table row-pointer" hide-default-footer hide-default-header
            loading-text="Carregando..." @click:row="editKnowledge">

            <template #header="{ props }">
              <thead>
                <tr v-if="props && props.headers">
                  <th class="text-center" :class="`col-${header.value}`" v-for="header in props.headers"
                    :key="header.value">
                    <div>
                      <span>{{ header.text }}</span>
                    </div>
                  </th>
                </tr>
              </thead>
            </template>

            <template #[`item.title`]="{ item }">
              <div class="d-flex align-center py-1">
                <v-icon x-small :color="getTypeColor(item.type)" class="mr-1">{{ getTypeIcon(item.type) }}</v-icon>
                <span class="font-weight-medium text-caption">{{ item.title }}</span>
              </div>
            </template>

            <template #[`item.type`]="{ item }">
              <v-chip x-small :color="getTypeColor(item.type)" text-color="#fff">
                {{ getTypeName(item.type) }}
              </v-chip>
            </template>

            <template #[`item.actions`]="{ item }">
              <div class="d-flex justify-center">
                <v-btn icon x-small class="ma-1" color="#0F3565" @click.stop="editKnowledge(item)">
                  <v-icon x-small>mdi-pencil-outline</v-icon>
                </v-btn>
                <v-btn icon x-small class="ma-1" color="error" @click.stop="openDeleteDialog(item)">
                  <v-icon x-small>mdi-trash-can-outline</v-icon>
                </v-btn>
              </div>
            </template>

            <template #no-data>
              <div class="text-center py-6">
                <v-icon size="48" color="#BDBDBD" class="mb-3">mdi-book-open-variant</v-icon>
                <div class="text-body-2 grey--text mb-3">Nenhum conhecimento cadastrado</div>
                <v-btn small color="#0F3565" dark depressed @click="openDialog">
                  <v-icon left x-small>mdi-plus</v-icon>
                  Adicionar
                </v-btn>
              </div>
            </template>
          </v-data-table>

          <div class="pagination" v-if="knowledges.length > 0">
            <div class="item-pagination block-one">
              <v-menu bottom offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" class="btn" depressed color="#C0C0C0" small>
                    qtd: {{ itemsPerPage }}
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item v-for="(size, i) in sizes" :key="i" @click="setPageSize(size)">
                    <v-list-item-title>{{ size }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
              <div class="registros" v-if="totalItensOnPage">
                <span> {{ paginationStart }} - {{ paginationEnd }} de {{ totalItems }} </span>
              </div>
            </div>

            <div class="item-pagination block-two">
              <v-pagination class="pagination-item" v-model="page" :length="totalPages"
                :total-visible="3"></v-pagination>
            </div>
          </div>
        </div>
      </v-card>
    </div>

    <!-- ======= DIALOG ADICIONAR (desktop) ======= -->
    <v-dialog v-model="dialog" max-width="900px" v-if="$vuetify.breakpoint.mdAndUp">
      <v-card class="pa-4" outlined>
        <div class="d-flex py-2 align-center">
          <v-spacer></v-spacer>
          <h3 class="dialog-title-add">Adicionar conhecimento</h3>
          <v-spacer></v-spacer>
          <v-btn icon absolute top right @click="closeCreateDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-divider class="my-4"></v-divider>
        <v-form ref="createForm" @submit.prevent="createKnowledge" class="dialog-form">
          <v-row class="ma-0">
            <v-col cols="12" md="8">
              <label class="label">Titulo <span class="required">*</span></label>
              <v-text-field v-model="form.title" placeholder="Ex: Como abrir um chamado"
                :rules="rules.required" outlined background-color="#F5F5F5" class="primary-input"
                hide-details="auto" counter="200" maxlength="200"></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <label class="label">Tipo <span class="required">*</span></label>
              <v-select v-model="form.type" :items="knowledgeTypes" item-text="text" item-value="value"
                placeholder="Selecione o tipo" :rules="rules.requiredSelect" outlined background-color="#F5F5F5"
                class="primary-input" hide-details="auto" append-icon="mdi-chevron-down"></v-select>
            </v-col>
            <v-col cols="12">
              <label class="label">Conteudo <span class="required">*</span></label>
              <v-textarea v-model="form.content" placeholder="Descreva detalhadamente o conteudo que o Felix deve usar para responder perguntas..."
                :rules="rules.required" outlined background-color="#F5F5F5" class="primary-input"
                hide-details="auto" rows="6" auto-grow counter></v-textarea>
            </v-col>
            <v-col cols="12" class="d-flex align-end justify-end pt-2 pb-4">
              <v-btn class="secondary-button" depressed dark @click="closeCreateDialog" :disabled="saving">Cancelar</v-btn>
              <v-btn type="submit" class="ml-3 primary-button" depressed dark :loading="saving" :disabled="!isCreateFormValid">
                <v-icon left small>mdi-check</v-icon>
                Adicionar
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </v-dialog>

    <!-- ======= DIALOG ADICIONAR (mobile) ======= -->
    <v-dialog v-model="dialog" max-width="100vw" persistent v-if="$vuetify.breakpoint.smAndDown"
      transition="dialog-bottom-transition">
      <v-card class="pa-2" outlined color="#fff">
        <div style="display: flex; justify-content: flex-end;">
          <v-btn icon @click="closeCreateDialog" style="margin-right: 8px;">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <div style="padding: 0 8px;">
          <h3 class="dialog-title-add">Adicionar conhecimento</h3>
        </div>
        <v-divider class="my-2"></v-divider>
        <v-form ref="createFormMobile" @submit.prevent="createKnowledge" class="dialog-form-mobile">
          <v-row class="ma-0">
            <v-col cols="12" class="pb-1">
              <label class="label">Titulo <span class="required">*</span></label>
              <v-text-field v-model="form.title" placeholder="Ex: Como abrir um chamado"
                :rules="rules.required" outlined background-color="#F5F5F5" class="primary-input"
                hide-details="auto" dense></v-text-field>
            </v-col>
            <v-col cols="12" class="pb-1">
              <label class="label">Tipo <span class="required">*</span></label>
              <v-select v-model="form.type" :items="knowledgeTypes" item-text="text"
                item-value="value" placeholder="Selecione o tipo" :rules="rules.requiredSelect" outlined
                background-color="#F5F5F5" class="primary-input" hide-details="auto" dense
                append-icon="mdi-chevron-down"></v-select>
            </v-col>
            <v-col cols="12" class="pb-1">
              <label class="label">Conteudo <span class="required">*</span></label>
              <v-textarea v-model="form.content" placeholder="Descreva o conteudo..."
                :rules="rules.required" outlined background-color="#F5F5F5" class="primary-input"
                hide-details="auto" rows="4" auto-grow dense></v-textarea>
            </v-col>
            <v-col cols="12" class="d-flex justify-end pt-2 pb-3">
              <v-btn class="secondary-button" depressed dark @click="closeCreateDialog" :disabled="saving" small>Cancelar</v-btn>
              <v-btn type="submit" class="ml-3 primary-button" depressed dark :loading="saving" :disabled="!isCreateFormValid" small>
                Adicionar
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </v-dialog>

    <!-- ======= DIALOG EXCLUIR ======= -->
    <v-dialog v-model="deleteDialog" max-width="420px">
      <v-sheet class="px-7 pt-7 pb-4 text-center w-100 rounded-lg" color="blue-grey darken-3" dark>
        <v-icon size="40" color="error" class="mb-3">mdi-alert-circle-outline</v-icon>
        <div class="text-body-1 mb-1 font-weight-medium">Excluir conhecimento</div>
        <div class="grey--text text--lighten-1 text-body-2 mb-5">
          Tem certeza que deseja excluir <strong>"{{ deleteTitle }}"</strong>?<br>
          O Felix deixara de usar este conteudo nas respostas.
        </div>
        <v-btn :disabled="saving" class="ma-1" color="grey" text @click="deleteDialog = false">
          Cancelar
        </v-btn>
        <v-btn :loading="saving" class="ma-1" color="error" text @click="confirmDeleteKnowledge">
          <v-icon left small>mdi-trash-can-outline</v-icon>
          Excluir
        </v-btn>
      </v-sheet>
    </v-dialog>

    <!-- ======= DIALOG EDITAR (desktop) ======= -->
    <v-dialog v-model="editDialog" max-width="900px" v-if="$vuetify.breakpoint.mdAndUp">
      <v-card class="pa-4" outlined>
        <div class="d-flex py-2 align-center">
          <v-spacer></v-spacer>
          <h3 class="dialog-title-add">Editar conhecimento</h3>
          <v-spacer></v-spacer>
          <v-btn icon absolute top right @click="editDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-divider class="my-4"></v-divider>
        <v-form ref="editFormRef" @submit.prevent="saveEditKnowledge" class="dialog-form">
          <v-row class="ma-0">
            <v-col cols="12" md="8">
              <label class="label">Titulo <span class="required">*</span></label>
              <v-text-field v-model="editFormData.title" placeholder="Ex: Como abrir um chamado"
                :rules="rules.required" outlined background-color="#F5F5F5" class="primary-input"
                hide-details="auto" counter="200" maxlength="200"></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <label class="label">Tipo <span class="required">*</span></label>
              <v-select v-model="editFormData.type" :items="knowledgeTypes" item-text="text"
                item-value="value" placeholder="Selecione o tipo" :rules="rules.requiredSelect" outlined
                background-color="#F5F5F5" class="primary-input" hide-details="auto"
                append-icon="mdi-chevron-down"></v-select>
            </v-col>
            <v-col cols="12">
              <label class="label">Conteudo <span class="required">*</span></label>
              <v-textarea v-model="editFormData.content" placeholder="Descreva detalhadamente o conteudo..."
                :rules="rules.required" outlined background-color="#F5F5F5" class="primary-input"
                hide-details="auto" rows="6" auto-grow counter></v-textarea>
            </v-col>
            <v-col cols="6" md="4">
              <label class="label">Status</label>
              <v-select v-model="editFormData.isActive" :items="statusOptions"
                item-text="text" item-value="value" hide-details="auto" outlined
                background-color="#F5F5F5" class="primary-input" append-icon="mdi-chevron-down"></v-select>
            </v-col>
            <v-col cols="6" md="8" class="d-flex align-end justify-space-between">
              <v-btn color="error" text @click="openDeleteDialog(editFormData)" :disabled="saving">
                <v-icon left small>mdi-trash-can-outline</v-icon>
                Excluir
              </v-btn>
              <div>
                <v-btn class="secondary-button" depressed dark @click="editDialog = false" :disabled="saving">Cancelar</v-btn>
                <v-btn class="ml-3 primary-button" depressed dark type="submit" :loading="saving" :disabled="!isEditFormValid">
                  <v-icon left small>mdi-check</v-icon>
                  Salvar
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </v-dialog>

    <!-- ======= DIALOG EDITAR (mobile) ======= -->
    <v-dialog v-model="editDialog" max-width="100vw" persistent v-if="$vuetify.breakpoint.smAndDown"
      transition="dialog-bottom-transition">
      <v-card class="pa-2" outlined color="#fff">
        <div style="display: flex; justify-content: flex-end;">
          <v-btn icon @click="editDialog = false" style="margin-right: 8px;">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <div style="padding: 0 8px;">
          <h3 class="dialog-title-add">Editar conhecimento</h3>
        </div>
        <v-divider class="my-2"></v-divider>
        <v-form ref="editFormMobileRef" @submit.prevent="saveEditKnowledge" class="dialog-form-mobile">
          <v-row class="ma-0">
            <v-col cols="12" class="pb-1">
              <label class="label">Titulo <span class="required">*</span></label>
              <v-text-field v-model="editFormData.title" placeholder="Ex: Como abrir um chamado"
                :rules="rules.required" outlined background-color="#F5F5F5" class="primary-input"
                hide-details="auto" dense></v-text-field>
            </v-col>
            <v-col cols="12" class="pb-1">
              <label class="label">Tipo <span class="required">*</span></label>
              <v-select v-model="editFormData.type" :items="knowledgeTypes" item-text="text"
                item-value="value" placeholder="Selecione o tipo" :rules="rules.requiredSelect" outlined
                background-color="#F5F5F5" class="primary-input" hide-details="auto" dense
                append-icon="mdi-chevron-down"></v-select>
            </v-col>
            <v-col cols="12" class="pb-1">
              <label class="label">Conteudo <span class="required">*</span></label>
              <v-textarea v-model="editFormData.content" placeholder="Descreva o conteudo..."
                :rules="rules.required" outlined background-color="#F5F5F5" class="primary-input"
                hide-details="auto" rows="4" auto-grow dense></v-textarea>
            </v-col>
            <v-col cols="12" class="pb-1">
              <label class="label">Status</label>
              <v-select v-model="editFormData.isActive" :items="statusOptions"
                item-text="text" item-value="value" hide-details="auto" outlined dense
                background-color="#F5F5F5" class="primary-input" append-icon="mdi-chevron-down"></v-select>
            </v-col>
            <v-col cols="12" class="d-flex justify-space-between pt-2 pb-3">
              <v-btn small text color="error" @click="openDeleteDialog(editFormData)" :disabled="saving">
                <v-icon x-small left>mdi-trash-can-outline</v-icon>
                Excluir
              </v-btn>
              <div>
                <v-btn class="secondary-button" depressed dark @click="editDialog = false" :disabled="saving" small>Cancelar</v-btn>
                <v-btn type="submit" class="ml-2 primary-button" depressed dark :loading="saving" :disabled="!isEditFormValid" small>
                  Salvar
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<style scoped lang="scss">
.mobile {
  display: none;
  @media (max-width: 960px) {
    display: block;
  }
}

.desktop {
  @media (max-width: 960px) {
    display: none;
  }
}

.mt-4 > .v-card.pa-4.mb-6 {
  box-shadow: none !important;
  border: none !important;
  background: transparent !important;
}

// Row click cursor
.row-pointer ::v-deep tbody tr:hover {
  cursor: pointer;
  background: rgba(15, 53, 101, 0.04) !important;
}

// Action buttons only visible on hover (desktop)
@media (min-width: 961px) {
  .actions-cell {
    opacity: 0.3;
    transition: opacity 0.15s ease;
  }
  .row-pointer ::v-deep tbody tr:hover .actions-cell {
    opacity: 1;
  }
}

.title-cell {
  line-height: 1.3;
}

.content-cell {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 400px;
  line-height: 1.4;
  color: #616161;
  font-size: 0.875rem;
}

.required {
  color: #F44336;
  font-weight: 600;
}

// Desktop column widths
@media (min-width: 961px) {
  .col-title {
    width: 24% !important;
    max-width: 24% !important;
    min-width: 140px;
  }
  .col-content {
    width: 36% !important;
    max-width: 36% !important;
    min-width: 180px;
  }
  .col-type {
    width: 12% !important;
    max-width: 12% !important;
    min-width: 100px;
    text-align: center;
  }
  .col-isActive {
    width: 9% !important;
    max-width: 9% !important;
    min-width: 80px;
    text-align: center;
  }
  .col-createdAt {
    width: 9% !important;
    max-width: 9% !important;
    min-width: 80px;
    text-align: center;
  }
  .col-actions {
    width: 10% !important;
    max-width: 10% !important;
    min-width: 90px;
    text-align: center;
  }
}

// Mobile column widths
@media (max-width: 960px) {
  .col-title {
    width: 40% !important;
    max-width: 40% !important;
    min-width: 100px;
  }
  .col-type {
    width: 30% !important;
    max-width: 30% !important;
    min-width: 80px;
    text-align: center;
  }
  .col-actions {
    width: 30% !important;
    max-width: 30% !important;
    min-width: 80px;
    text-align: center;
  }
}

@media (max-width: 600px) {
  .wrapper.mobile {
    overflow-x: auto;
  }
  .main-table {
    font-size: 13px !important;
  }
  .main-table th,
  .main-table td {
    padding: 4px 6px !important;
    min-width: 80px !important;
    word-break: break-word;
  }
  .content-cell {
    max-width: 150px;
    -webkit-line-clamp: 1;
  }
}

.dialog-title-add {
  width: 100%;
  text-align: center;
  display: block;
  margin-top: 0;
  font-size: 1.4rem;
  font-weight: 600;
  color: #0F3565;
}

@media (max-width: 600px) {
  .dialog-title-add {
    margin-top: 8px;
    margin-bottom: 8px;
    font-size: 1.2rem;
  }
}

.dialog-form {
  padding: 16px 32px 0 32px;
}

.dialog-form-mobile {
  padding: 8px 12px 0 12px;
}

.label {
  font-weight: 500;
  font-size: 0.85rem;
  color: #424242;
  margin-bottom: 4px;
  display: inline-block;
}
</style>

<script>
import ApiService from '@/services/ApiService'
const apiService = new ApiService()

const TYPE_MAP = {
  3: { text: 'Regra de negocio', color: '#0F3565', icon: 'mdi-gavel' },
  4: { text: 'Documentacao', color: '#FF9800', icon: 'mdi-file-document-outline' },
  5: { text: 'Tutorial', color: '#BBCF20', icon: 'mdi-school-outline' },
}

export default {
  data() {
    return {
      deleteDialog: false,
      deleteId: null,
      deleteTitle: '',
      dialog: false,
      editDialog: false,
      loading: true,
      saving: false,
      searchQuery: '',
      activeSearchQuery: '',
      page: 1,
      totalPages: 1,
      itemsPerPage: 10,
      sizes: [10, 20, 50],
      totalItems: 0,
      totalItensOnPage: 0,
      form: {
        title: '',
        content: '',
        type: '',
        isActive: true
      },
      editFormData: {
        id: null,
        title: '',
        content: '',
        type: '',
        isActive: true
      },
      visible: false,
      filterActive: null,
      filterType: null,
      filterOptions: [
        { text: 'Todos', value: null },
        { text: 'Ativos', value: true },
        { text: 'Inativos', value: false }
      ],
      filterTypeOptions: [
        { value: 3, text: 'Regra de negocio' },
        { value: 4, text: 'Documentacao' },
        { value: 5, text: 'Tutorial' },
      ],
      statusOptions: [
        { text: 'Ativo', value: true },
        { text: 'Inativo', value: false }
      ],
      knowledges: [],
      headers: [
        { text: 'Titulo', value: 'title', align: 'start' },
        { text: 'Conteudo', value: 'content', align: 'start' },
        { text: 'Tipo', value: 'type', align: 'center' },
        { text: 'Status', value: 'isActive', align: 'center' },
        { text: 'Data', value: 'createdAt', align: 'center' },
        { text: 'Acoes', value: 'actions', align: 'center', sortable: false }
      ],
      headersMobile: [
        { text: 'Titulo', value: 'title', align: 'start' },
        { text: 'Tipo', value: 'type', align: 'center' },
        { text: 'Acoes', value: 'actions', align: 'center', sortable: false }
      ],
      knowledgeTypes: [
        { value: 3, text: 'Regra de negocio' },
        { value: 4, text: 'Documentacao' },
        { value: 5, text: 'Tutorial' },
      ],
      rules: {
        required: [v => !!v && v.trim().length > 0 || 'Campo obrigatorio'],
        requiredSelect: [v => v !== '' && v !== null && v !== undefined || 'Campo obrigatorio'],
      },
    }
  },

  computed: {
    hasActiveFilters() {
      return this.activeSearchQuery || this.filterType !== null || this.filterActive !== null
    },
    isCreateFormValid() {
      return this.form.title?.trim() && this.form.content?.trim() && this.form.type !== '' && this.form.type !== null && this.form.type !== undefined
    },
    isEditFormValid() {
      return this.editFormData.title?.trim() && this.editFormData.content?.trim() && this.editFormData.type !== '' && this.editFormData.type !== null && this.editFormData.type !== undefined
    },
    paginationStart() {
      return ((this.page - 1) * this.itemsPerPage) + 1
    },
    paginationEnd() {
      return Math.min(this.page * this.itemsPerPage, this.totalItems)
    },
  },

  async created() {
    this.userLogged = JSON.parse(localStorage.getItem('user'))
    await this.updateTable()
  },

  methods: {
    getTypeName(type) {
      return TYPE_MAP[type]?.text || 'Outro'
    },
    getTypeColor(type) {
      return TYPE_MAP[type]?.color || '#9E9E9E'
    },
    getTypeIcon(type) {
      return TYPE_MAP[type]?.icon || 'mdi-help-circle-outline'
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString('pt-BR')
    },

    async getKnowledgeBasePaginated(filter = {}) {
      const response = await apiService.get('knowledgeBase/paginate', filter, true, false)
      return response.content
    },

    async updateTable() {
      this.loading = true
      try {
        const query = {
          Query: this.activeSearchQuery || '',
          Page: this.page,
          PageSize: this.itemsPerPage
        }
        if (this.filterActive !== null) query.IsActive = this.filterActive
        const data = await this.getKnowledgeBasePaginated(query)
        this.knowledges = Array.isArray(data.data) ? data.data : []
        this.totalItems = data.pager?.totalItems || this.knowledges.length
        this.totalItensOnPage = this.knowledges.length
        this.totalPages = data.pager?.totalPages || 1
      } catch {
        // silently fail on load
      }
      this.loading = false
    },

    async searchKnowledge() {
      this.page = 1
      this.activeSearchQuery = (this.searchQuery || '').trim()
      await this.updateTable()
    },

    clearSearch() {
      this.searchQuery = ''
      this.activeSearchQuery = ''
      this.page = 1
      this.updateTable()
    },

    clearAllFilters() {
      this.searchQuery = ''
      this.activeSearchQuery = ''
      this.filterType = null
      this.filterActive = null
      this.page = 1
      this.updateTable()
    },

    async confirmDeleteKnowledge() {
      if (!this.deleteId) return
      this.saving = true
      try {
        await apiService.delete(`knowledgeBase/${this.deleteId}`, true, false)
        this.deleteDialog = false
        this.editDialog = false
        this.$toast.success('Conhecimento removido!😿​')
        await this.updateTable()
      } catch {
        this.$toast.error('Erro ao excluir conhecimento')
      }
      this.saving = false
    },

    async saveEditKnowledge() {
      const form = this.$refs.editFormRef || this.$refs.editFormMobileRef
      if (form && !form.validate()) return

      this.saving = true
      try {
        const payload = {
          Title: this.editFormData.title,
          Content: this.editFormData.content,
          Type: this.editFormData.type,
          IsActive: this.editFormData.isActive,
          UpdatedBy: this.userLogged?.id
        }
        await apiService.put(`knowledgeBase/${this.editFormData.id}`, payload, true, false)
        this.editDialog = false
        this.$toast.success('Conhecimento atualizado! O Felix agradece 😺')
        await this.updateTable()
      } catch {
        this.$toast.error('Erro ao editar conhecimento')
      }
      this.saving = false
    },

    async createKnowledge() {
      const form = this.$refs.createForm || this.$refs.createFormMobile
      if (form && !form.validate()) return

      this.saving = true
      try {
        const payload = {
          Title: this.form.title,
          Content: this.form.content,
          Type: this.form.type,
          IsActive: true,
        }
        await apiService.post('knowledgeBase/create', payload, true, false)
        this.dialog = false
        this.$toast.success('Conhecimento adicionado! O Felix aprendeu algo novo😺🧠')
        await this.updateTable()
      } catch {
        this.$toast.error('Erro ao criar conhecimento')
      }
      this.saving = false
    },

    async applyFilter() {
      this.page = 1
      await this.updateTable()
    },

    setPageSize(size) {
      this.itemsPerPage = size
      this.page = 1
      this.updateTable()
    },

    openDialog() {
      this.form = { title: '', content: '', type: '', isActive: true }
      this.dialog = true
      this.$nextTick(() => {
        const form = this.$refs.createForm || this.$refs.createFormMobile
        if (form) form.resetValidation()
      })
    },

    closeCreateDialog() {
      this.dialog = false
      this.form = { title: '', content: '', type: '', isActive: true }
    },

    editKnowledge(knowledge) {
      this.editFormData = {
        id: knowledge.id || '',
        title: knowledge.title || '',
        content: knowledge.content || '',
        type: knowledge.type !== undefined && knowledge.type !== null ? Number(knowledge.type) : '',
        isActive: knowledge.isActive === true || knowledge.isActive === 'true' || knowledge.isActive === 1
      }
      this.editDialog = true
      this.$nextTick(() => {
        const form = this.$refs.editFormRef || this.$refs.editFormMobileRef
        if (form) form.resetValidation()
      })
    },

    openDeleteDialog(knowledge) {
      this.deleteId = knowledge.id
      this.deleteTitle = knowledge.title || 'este item'
      this.deleteDialog = true
    },
  },

  watch: {
    page() {
      this.updateTable()
    },
    itemsPerPage() {
      this.updateTable()
    },
  },
}
</script>
